import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageContent from "../shared/PageContent/PageContent";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import TrainingTabHomePage from "./TrainingTabHomePage";
import TrainingTabAllCoursesPage from "./TrainingTabAllCoursesPage";
import TrainingTabAllProgramsPage from "./TrainingTabAllProgramsPage";
import TrainingTabAllVideosPage from "./TrainingTabAllVideosPage";
import TrainingTabPitchReviewPage from "./TrainingTabPitchReviewPage";
import "./TrainingPage.css";

const TRAINING_TABS = [
  "Home",
  "All Courses",
  "All Programs",
  "All Videos",
  "Pitch Review",
];

const TAB_TO_QUERY = {
  Home: "home",
  "All Courses": "courses",
  "All Programs": "programs",
  "All Videos": "videos",
  "Pitch Review": "pitch-review",
};

const QUERY_TO_TAB = {
  home: "Home",
  courses: "All Courses",
  programs: "All Programs",
  videos: "All Videos",
  "pitch-review": "Pitch Review",
};

const getTrainingTabFromSearch = search => {
  const params = new URLSearchParams(search);
  const tabParam = params.get("tab");
  return QUERY_TO_TAB[tabParam] || "Home";
};

function TrainingMainPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    getTrainingTabFromSearch(location.search)
  );
  const [streakData, setStreakData] = useState({ streak_days: 0, week: [] });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchHeaderData = async () => {
    try {
      setIsRefreshing(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/training/streaks"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch training header data");
      }

      const result = await response.json();
      setStreakData(result?.data || { streak_days: 0, week: [] });
    } catch (error) {
      setStreakData({ streak_days: 0, week: [] });
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const tabFromUrl = getTrainingTabFromSearch(location.search);
    setActiveTab(prevTab => (prevTab === tabFromUrl ? prevTab : tabFromUrl));
  }, [location.search]);

  useEffect(() => {
    fetchHeaderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentTabParam = new URLSearchParams(location.search).get("tab");
    const nextTabParam = TAB_TO_QUERY[activeTab] || "home";

    if (currentTabParam !== nextTabParam) {
      navigate(
        {
          pathname: location.pathname,
          search: `?tab=${nextTabParam}`,
        },
        { replace: true }
      );
    }
  }, [activeTab, location.pathname, location.search, navigate]);

  const renderActiveTabPage = () => {
    switch (activeTab) {
      case "All Courses":
        return <TrainingTabAllCoursesPage />;
      case "All Programs":
        return <TrainingTabAllProgramsPage />;
      case "All Videos":
        return <TrainingTabAllVideosPage />;
      case "Pitch Review":
        return <TrainingTabPitchReviewPage />;
      case "Home":
      default:
        return <TrainingTabHomePage />;
    }
  };

  return (
    <PageContent
      customHeader={
        <div className="training-page-custom-header">
          <div className="training-page-header-left">
            <h1 className="training-page-main-title">Training</h1>
            <div className="training-page-nav-tabs">
              {TRAINING_TABS.map(tab => (
                <button
                  key={tab}
                  type="button"
                  className={`training-page-nav-tab ${
                    activeTab === tab ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <div className="training-page-header-right">
            <div className="training-page-streak-counter">
              <span className="training-page-streak-icon">🔥</span>
              <span className="training-page-streak-text">
                {streakData.streak_days} day streak
              </span>
            </div>
            <div className="training-page-week-tracker">
              <div className="training-page-streak-days">
                {(streakData.week || []).map((dayData, index) => (
                  <div key={index} className="training-page-streak-day">
                    <span
                      className={`training-page-streak-circle ${
                        dayData.completed ? "completed" : ""
                      }`}
                    >
                      {dayData.completed && (
                        <svg
                          className="training-page-streak-check"
                          width="10"
                          height="10"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="training-page-streak-day-label">
                      {dayData.weekday ||
                        ["S", "M", "T", "W", "T", "F", "S"][index]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div
              className="training-page-notification-bell"
              onClick={() => navigate("/pitch-reviews")}
              style={{ cursor: "pointer" }}
              title="Manage pitch reviews"
            >
              <svg
                className="training-page-bell-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 4 0 0 1-3.46 0"></path>
              </svg>
            </div>
            <button
              className="training-page-refresh-button"
              onClick={fetchHeaderData}
              disabled={isRefreshing}
              title="Sync"
              style={{
                background: "none",
                border: "none",
                cursor: isRefreshing ? "not-allowed" : "pointer",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isRefreshing ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-text-secondary)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                }}
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            </button>
          </div>
        </div>
      }
    >
      {renderActiveTabPage()}
    </PageContent>
  );
}

export default TrainingMainPage;
